// =============================================================================
// publishHtmlAsWpPage — publikuje dowolny HTML jako STRONĘ WP (post_type=page).
// Wspólny helper: (a) unikalne podstrony, (b) strona autora.
// Restore-pattern na credentials wordpressService (zapisz prev → set → finally restore).
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import wordpressService from "@/services/wordpressService";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";

export interface PublishPageResult {
  ok: boolean;
  wpPageId?: number;
  url?: string;
  error?: string;
}

const PL_MAP: Record<string, string> = { ą: "a", ć: "c", ę: "e", ł: "l", ń: "n", ó: "o", ś: "s", ź: "z", ż: "z" };

export function slugFromTitle(title: string): string {
  return (title || "strona")
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (c) => PL_MAP[c] || c)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "strona";
}

async function withWebsiteCreds<T>(websiteId: string, fn: () => Promise<T>): Promise<T> {
  const { data: site } = await (supabase as any)
    .from("websites")
    .select("url, login, password, api_key")
    .eq("id", websiteId)
    .maybeSingle();
  if (!site?.url) throw new Error("Brak danych domeny (websites)");

  const prev = (wordpressService as any).getCredentials?.() ?? null;
  (wordpressService as any).setCredentials({
    url: site.url,
    username: site.login,
    password: site.api_key || site.password,
  });
  try {
    return await fn();
  } finally {
    if (prev) (wordpressService as any).setCredentials(prev);
  }
}

export async function publishHtmlAsWpPage(args: {
  websiteId: string;
  title: string;
  html: string;
  slug?: string;
}): Promise<PublishPageResult> {
  try {
    if (!args.html || args.html.trim().length < 20) return { ok: false, error: "Pusta treść HTML" };
    return await withWebsiteCreds(args.websiteId, async () => {
      const res = await wordpressService.createPage({
        title: args.title,
        content: args.html,
        status: "publish",
        slug: args.slug || slugFromTitle(args.title),
      });
      if (!res.success || !res.data) return { ok: false, error: res.error || "Publikacja nie powiodła się" };
      logPublic("UNIQUE-PUBLISH", `Opublikowano stronę WP: ${args.title} → ${res.data.url}`);
      return { ok: true, wpPageId: res.data.postId, url: res.data.url };
    });
  } catch (e) {
    logError("UNIQUE-PUBLISH", `publishHtmlAsWpPage error`, e);
    return { ok: false, error: e instanceof Error ? e.message : "wyjątek" };
  }
}

/** Publikuje podstronę z unique_subpages i zapisuje wynik. */
export async function publishSubpageToWp(subpageId: string, websiteId: string): Promise<PublishPageResult> {
  const { data: sp } = await (supabase as any)
    .from("unique_subpages")
    .select("id, title, html")
    .eq("id", subpageId)
    .maybeSingle();
  if (!sp) return { ok: false, error: "Nie znaleziono podstrony" };
  if (!sp.html) return { ok: false, error: "Podstrona nie ma treści (najpierw wygeneruj)" };

  const res = await publishHtmlAsWpPage({ websiteId, title: sp.title, html: sp.html });
  if (res.ok) {
    await (supabase as any)
      .from("unique_subpages")
      .update({ status: "published", wp_page_id: res.wpPageId ?? null, wp_page_url: res.url ?? null, updated_at: new Date().toISOString() })
      .eq("id", subpageId);
  }
  return res;
}
